import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Shield, Award, CheckCircle, Users, BookOpen, TrendingUp, Star, Play } from "lucide-react";
import { Button } from "../components/ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BBI_LOGO = "https://customer-assets.emergentagent.com/job_54aeb419-6630-419e-b73e-06cd0626cb80/artifacts/xw2ii5qd_AD1A694D-1FDF-43F3-8C4C-B0BC17C37F1E.png";
const HERO_IMAGE = "https://images.unsplash.com/photo-1758518727707-b023e285b709?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxjb25maWRlbnQlMjBidXNpbmVzcyUyMG93bmVyJTIwZGl2ZXJzZSUyMHRlYW0lMjBtZWV0aW5nJTIwcHJvZmVzc2lvbmFsJTIwb2ZmaWNlfGVufDB8fHx8MTc3MDcxNDQ2OHww&ixlib=rb-4.1.0&q=85";

const HomePage = () => {
  const [services, setServices] = useState([]);
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, coursesRes] = await Promise.all([
          axios.get(`${API_URL}/api/services`),
          axios.get(`${API_URL}/api/courses`)
        ]);
        setServices(servicesRes.data.slice(0, 3));
        setCourses(coursesRes.data.slice(0, 3));
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1E3A5F] via-[#162c47] to-[#0f1e32] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Hero Text */}
            <div className="lg:col-span-7 space-y-8 animate-fade-in">
              <div className="flex items-center gap-4 flex-wrap">
                <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-white/10 text-white border border-white/20">
                  <Shield className="h-4 w-4 mr-2 text-[#9D9045]" />
                  Veteran Owned
                </span>
                <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-white/10 text-white border border-white/20">
                  <Award className="h-4 w-4 mr-2 text-[#9D9045]" />
                  NNA Certified
                </span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                Build Your Business<br />
                <span className="text-[#9D9045]">With Confidence</span>
              </h1>
              
              <p className="text-lg text-gray-300 max-w-xl leading-relaxed">
                From startup to success, we provide the consulting services, education, and resources you need to build a thriving business. Join hundreds of entrepreneurs who've transformed their dreams into reality.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/school">
                  <Button className="bg-[#9D9045] hover:bg-[#8a7e3c] text-white px-8 py-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all btn-hover" data-testid="hero-cta-school">
                    <BookOpen className="mr-2 h-5 w-5" />
                    Explore Business School
                  </Button>
                </Link>
                <Link to="/services">
                  <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#1E3A5F] px-8 py-6 text-lg font-medium transition-all" data-testid="hero-cta-services">
                    View Services
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
              
              <div className="flex items-center gap-8 pt-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-white">500+</div>
                  <div className="text-sm text-gray-400">Businesses Helped</div>
                </div>
                <div className="w-px h-12 bg-white/20" />
                <div className="text-center">
                  <div className="text-3xl font-bold text-white">98%</div>
                  <div className="text-sm text-gray-400">Success Rate</div>
                </div>
                <div className="w-px h-12 bg-white/20" />
                <div className="text-center">
                  <div className="text-3xl font-bold text-white">5+</div>
                  <div className="text-sm text-gray-400">Years Experience</div>
                </div>
              </div>
            </div>
            
            {/* Hero Image */}
            <div className="lg:col-span-5 relative animate-slide-in-right">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={HERO_IMAGE} 
                  alt="Business professionals in meeting" 
                  className="w-full h-[400px] lg:h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A5F]/40 to-transparent" />
              </div>
              
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-6 max-w-[200px] animate-scale-in">
                <div className="flex items-center gap-3 mb-2">
                  <div className="h-10 w-10 rounded-full bg-[#9D9045]/10 flex items-center justify-center">
                    <TrendingUp className="h-5 w-5 text-[#9D9045]" />
                  </div>
                  <span className="font-semibold text-[#1E3A5F]">Growth</span>
                </div>
                <p className="text-sm text-gray-600">Average 40% revenue increase for our clients</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-gray-50" data-testid="services-preview">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#9D9045] font-medium text-sm tracking-wider uppercase">What We Offer</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1E3A5F] mt-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Comprehensive Business Solutions
            </h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              From strategy to execution, we provide everything you need to start, grow, and manage your business successfully.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 stagger-children">
            {services.map((service, index) => (
              <div 
                key={service.id} 
                className="service-card group"
                data-testid={`service-card-${index}`}
              >
                <div className="h-48 rounded-lg overflow-hidden mb-6">
                  <img 
                    src={service.image_url} 
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <h3 className="text-xl font-semibold text-[#1E3A5F] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {service.title}
                </h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                  {service.description}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-[#9D9045] font-semibold">
                    ${service.price?.toFixed(2)}
                    <span className="text-gray-400 text-sm font-normal ml-1">
                      /{service.price_type?.replace('_', ' ')}
                    </span>
                  </span>
                  <Link to="/services">
                    <Button variant="ghost" className="text-[#1E3A5F] hover:text-[#9D9045] p-0">
                      Learn More <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/services">
              <Button className="bg-[#1E3A5F] hover:bg-[#162c47] text-white px-8 py-6 text-lg" data-testid="view-all-services-btn">
                View All Services <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Business School Section */}
      <section className="py-20 bg-white" data-testid="school-preview">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img 
                src={BBI_LOGO} 
                alt="Business Bootcamp Institute" 
                className="w-full max-w-md mx-auto"
              />
            </div>
            
            <div className="space-y-8">
              <div>
                <span className="text-[#9D9045] font-medium text-sm tracking-wider uppercase">Business Bootcamp Institute</span>
                <h2 className="text-3xl md:text-4xl font-bold text-[#1E3A5F] mt-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Digital Business Education
                </h2>
                <p className="text-gray-600 mt-4 leading-relaxed">
                  Access our comprehensive online courses designed for entrepreneurs at every stage. Learn at your own pace with expert-led video modules covering everything from business structure to credit building.
                </p>
              </div>
              
              <div className="space-y-4">
                {[
                  "Business Structure Fundamentals",
                  "Money Management 101",
                  "Business Planning Principles",
                  "Business Credit Building"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-[#9D9045]" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
              
              <div className="flex items-center gap-6">
                <Link to="/school">
                  <Button className="bg-[#1E3A5F] hover:bg-[#162c47] text-white px-6 py-6" data-testid="explore-courses-btn">
                    <Play className="mr-2 h-5 w-5" />
                    Explore Courses
                  </Button>
                </Link>
                <div className="text-sm text-gray-500">
                  <span className="text-[#1E3A5F] font-semibold text-lg">4</span> courses available
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-[#1E3A5F]" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#9D9045] font-medium text-sm tracking-wider uppercase">Testimonials</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              What Our Clients Say
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                quote: "OMW helped me navigate the complex process of forming my LLC. Their expertise saved me countless hours and potential legal headaches.",
                author: "Marcus J.",
                role: "Small Business Owner",
                rating: 5
              },
              {
                quote: "The Business Bootcamp courses gave me the confidence to finally start my dream business. The content is practical and easy to follow.",
                author: "Sarah T.",
                role: "Entrepreneur",
                rating: 5
              },
              {
                quote: "Professional, knowledgeable, and truly invested in my success. I couldn't have built my business credit without their guidance.",
                author: "David R.",
                role: "Startup Founder",
                rating: 5
              }
            ].map((testimonial, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-sm rounded-xl p-8 border border-white/10">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, j) => (
                    <Star key={j} className="h-5 w-5 fill-[#9D9045] text-[#9D9045]" />
                  ))}
                </div>
                <p className="text-gray-300 mb-6 italic">"{testimonial.quote}"</p>
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-full bg-[#9D9045]/20 flex items-center justify-center">
                    <Users className="h-6 w-6 text-[#9D9045]" />
                  </div>
                  <div>
                    <div className="font-semibold text-white">{testimonial.author}</div>
                    <div className="text-sm text-gray-400">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1E3A5F] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ready to Start Your Business Journey?
          </h2>
          <p className="text-gray-600 mb-8 text-lg">
            Take advantage of our free discovery consultation and learn how we can help you achieve your business goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-[#1E3A5F] hover:bg-[#162c47] text-white px-8 py-6 text-lg" data-testid="cta-contact-btn">
                Schedule Free Consultation
              </Button>
            </Link>
            <Link to="/register">
              <Button variant="outline" className="border-2 border-[#9D9045] text-[#9D9045] hover:bg-[#9D9045] hover:text-white px-8 py-6 text-lg" data-testid="cta-register-btn">
                Create Account
              </Button>
            </Link>
          </div>
          <p className="text-sm text-gray-500 mt-6">
            10% off Initial Business Startup Package with code "New Start"
          </p>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
